import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import { AuthStore } from '../stores/auth-store';
import type {
  TagListRequest,
  TagListResponse,
  TagCreateRequest,
  UpdatePaymentTagsRequest,
  TagMutationResponse,
} from '../models';

/**
 * TagApi — HTTP API calls for payment tags
 *
 * Handles all tag-related HTTP requests:
 * - Listing available merchant tags
 * - Creating new tags
 * - Updating the full tag list on a specific payment (add/remove in one call)
 *
 * Uses AuthStore for credentials (token, merchant_id).
 */
@Injectable({
  providedIn: 'root',
})
export class TagApi {
  private readonly http = inject(HttpClient);
  private readonly authStore = inject(AuthStore);

  private getCredentials(): { token: string; merchant_id: string } {
    return {
      token: this.authStore.getToken() ?? '',
      merchant_id: this.authStore.getMerchantId() ?? '',
    };
  }

  /**
   * Fetch all available tags for the merchant.
   */
  listTags(): Observable<TagListResponse> {
    const payload: TagListRequest = {
      ...this.getCredentials(),
      device_type: environment.deviceType,
    };
    return this.http.post<TagListResponse>(
      `${environment.apiUrl}/api/merchant/payment_tags/list`,
      payload,
    );
  }

  /**
   * Create a new tag under the merchant account.
   */
  createTag(tagName: string): Observable<TagMutationResponse> {
    const payload: TagCreateRequest = {
      ...this.getCredentials(),
      tag_name: tagName,
    };
    return this.http.post<TagMutationResponse>(
      `${environment.apiUrl}/api/merchant/payment_tags/create`,
      payload,
    );
  }

  /**
   * Update the complete tag list for a specific payment.
   * Sends the full final set of tags — the API reconciles adds and removes.
   */
  updatePaymentTags(paymentId: string, tags: string[]): Observable<TagMutationResponse> {
    const payload: UpdatePaymentTagsRequest = {
      ...this.getCredentials(),
      payment_id: paymentId,
      payment_tags: tags,
    };
    return this.http.post<TagMutationResponse>(
      `${environment.apiUrl}/api/gozem_money/v1/update_payment`,
      payload,
    );
  }
}
